import React from 'react';

import {useTranslation} from "react-i18next";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";

import {blueColor} from "../../../../themes/style";
import {ApplicationDetailsType} from "../types";
import {StyledAccordion, StyledEmailIcon, StyledPhoneAndroidIcon, StyledPhoneEnabledIcon} from "../../styled";
import {formatDateToLocale} from "../../utils";
import {finishStatuses, path} from "../../../Utils/const";
import ContactAction from "./ContactAction";
import {OAuthDataProps} from "../../../../context/type";
import ZoomPanRotateImage from "../ImageZoom/ZoomPanRotateImage";

type DetailsFooterProps = {
    applicationDetails: ApplicationDetailsType
    handleOpenNoteForEdit: () => void
    editorIsMe: boolean
    oauthData: OAuthDataProps
}

const DetailsFooter: React.FC<DetailsFooterProps> = (props) => {
    const {t} = useTranslation('common');

    const {
        applicationDetails,
        handleOpenNoteForEdit,
        editorIsMe,
        oauthData,
    } = props;

    const {
        recruitNote,
        recruiterNote,
        editorNote,
        salesPartner,
        recruiterSignatureDate,
        recruiterSignatureCity,
        status,
        recruiterSignature
    } = applicationDetails;

    return (
        <Grid container spacing={2}>
            {recruitNote
                ? <Grid item xs={12}>
                    <Typography variant="body1" sx={{mb: 3}}>{t("remarks")}</Typography>
                    <Typography variant="body1"
                                sx={{mb: 3}}>{recruitNote}</Typography>
                </Grid>
                : null}

            <Grid item xs={12}>
                <Typography variant="h6" sx={{mb: 3, color: blueColor}}>{t("sponsor")}</Typography>
            </Grid>

            <Grid item sx={{mb: 3}}>
                <StyledAccordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                        <Typography variant="body1">{`${salesPartner.name} - ${t("customer_number")}: ${salesPartner.customerId}`}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={3} direction="row">
                            <Box>
                                <Box sx={{mb: 3}}>
                                    <Typography variant="body1">{salesPartner.company}</Typography>
                                    <Typography variant="body1">{salesPartner.street}</Typography>
                                    <Typography variant="body1">{salesPartner.residence}</Typography>
                                </Box>
                            </Box>

                            <>
                                <Divider orientation="vertical"  flexItem/>
                                <Stack spacing={1}>
                                    { salesPartner?.mobile && <ContactAction href={salesPartner?.mobile} Icon={ <StyledPhoneAndroidIcon />} type="tel"/> }
                                    { salesPartner?.phone && <ContactAction href={salesPartner?.phone} Icon={<StyledPhoneEnabledIcon/>} type="tel"/> }
                                    <ContactAction href={salesPartner.email} Icon={<StyledEmailIcon/>} type="email"/>
                                </Stack>

                            </>
                        </Stack>
                    </AccordionDetails>
                </StyledAccordion>
            </Grid>

            <Grid item xs={6} md={4}>
                <Typography
                    variant="body1">{`${recruiterSignatureDate ? formatDateToLocale(recruiterSignatureDate, oauthData) : ""}${recruiterSignatureCity ? ", " + recruiterSignatureCity : ""}`}</Typography>
            </Grid>
            {recruiterNote
                ? <Grid item xs={12}>
                    <Typography variant="body1" sx={{mb: 3}}>{t("remarks")}</Typography>
                    <Typography variant="body1" sx={{mb: 3}}>{recruiterNote}</Typography>
                </Grid>
                : null}
            {recruiterSignature && recruiterSignature.endpoint && path ? <Grid item xs={12}> <Box sx={{width: "300px", height: "200px"}}>
                <ZoomPanRotateImage src={`${path}${recruiterSignature.endpoint}`} styles={{backgroundColor: "rgba(0, 0, 0, 0.08)"}} type="signature" mandate={applicationDetails.mandate}/></Box></Grid>: null}

            {editorNote
                ? <Grid item xs={12}>
                    <Box sx={{width: "95%", padding: "10px", backgroundColor: "#fafafa", borderRadius: "5px"}}>
                        <Typography variant="body1" sx={{mb: 3}}>{t("note")}</Typography>
                        <Typography variant="body1" sx={{mb: 3}}>{editorNote}</Typography>
                        <Box sx={{width: "95%", display: "flex", justifyContent: "flex-end"}}>
                            <Chip icon={<EditIcon/>} label={`${t("edit")}`}
                                  onClick={handleOpenNoteForEdit}
                                  clickable
                                  disabled={!editorIsMe || finishStatuses.includes(status)}/>
                        </Box>
                    </Box>
                </Grid>
                : null}
        </Grid>
    );
};

export default DetailsFooter;